// Study page: choose mode → step through a "due" deck → judge each card into
// one of four spaced-repetition categories (Öğrendim / Tekrar / Bilmiyorum /
// İlk defa). Stub cards (bundled but not enriched yet) trigger a background
// AI fetch and reactively swap into the displayed card.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowsClockwise, Check, X, ArrowsHorizontal, BookOpen,
  ArrowsClockwise as Reload, Sparkle,
} from '@phosphor-icons/react';
import Flashcard from '../components/Flashcard';
import {
  useStore, wordsApi, sessionsApi, buildDueDeck, isStub,
  CATEGORY_LABEL, CATEGORY_INTERVAL_DAYS,
} from '../lib/store';
import { generateWord } from '../lib/api';
import { STUDY } from '../constants/testIds';
import { Button } from '../components/ui/button';

const SESSION_LIMIT = 20;

const CATEGORY_BUTTONS = [
  { key: 'learned', label: 'Öğrendim',         hint: 'ayda 1-2 tekrar', cls: 'bg-emerald-600 hover:bg-emerald-700 border-emerald-600',       testId: STUDY.learnedBtn },
  { key: 'review',  label: 'Kolay',            hint: 'ayda 3-4 tekrar', cls: 'bg-sky-400    hover:bg-sky-500    border-sky-400 text-black', testId: STUDY.reviewBtn },
  { key: 'new',     label: 'İlk Defa Gördüm',  hint: 'ayda 5-6 tekrar', cls: 'bg-orange-500 hover:bg-orange-600 border-orange-500 text-black', testId: STUDY.newBtn },
  { key: 'unknown', label: 'Zor',              hint: 'ayda 7-8 tekrar', cls: 'bg-rose-600   hover:bg-rose-700   border-rose-600',           testId: STUDY.unknownBtn },
];

export default function StudyPage() {
  const { words } = useStore();
  const [mode, setMode] = useState(null); // 'de-tr' | 'tr-de'
  const [started, setStarted] = useState(false);
  const [deck, setDeck] = useState([]);
  const [idx, setIdx] = useState(0);
  const [stage, setStage] = useState(0);
  const [tally, setTally] = useState({ learned: 0, review: 0, unknown: 0, new: 0 });
  const fetchingRef = useRef(new Set());

  // Pick up freshly-enriched words so the current card re-renders
  const currentRaw = deck[idx];
  const current = useMemo(() => {
    if (!currentRaw) return null;
    return words.find((w) => w.id === currentRaw.id) || currentRaw;
  }, [currentRaw, words]);

  // Lazy AI enrichment for current and next card
  useEffect(() => {
    if (!started) return;
    const toEnrich = [current, deck[idx + 1], deck[idx + 2]].filter(Boolean);
    toEnrich.forEach((w) => {
      const fresh = words.find((x) => x.id === w.id) || w;
      if (!isStub(fresh)) return;
      if (fetchingRef.current.has(fresh.id)) return;
      fetchingRef.current.add(fresh.id);
      wordsApi.update(fresh.id, { loading: true });
      generateWord(fresh.german, fresh.type || 'auto')
        .then((data) => {
          wordsApi.update(fresh.id, {
            type: data.type || fresh.type,
            turkish: data.turkish,
            article: data.article ?? null,
            plural: data.plural ?? null,
            sentence: data.sentence ?? null,
            sentences: data.sentences ?? null,
            praesens: data.praesens ?? null,
            praeteritum: data.praeteritum ?? null,
            perfekt: data.perfekt ?? null,
            loading: false,
          });
        })
        .catch((err) => {
          console.error('enrich failed', fresh.german, err);
          wordsApi.update(fresh.id, { loading: false });
        })
        .finally(() => fetchingRef.current.delete(fresh.id));
    });
  }, [started, idx, current, deck, words]);

  const onStart = (m) => {
    setMode(m);
    setDeck(buildDueDeck(words, SESSION_LIMIT));
    setIdx(0);
    setStage(0);
    setTally({ learned: 0, review: 0, unknown: 0, new: 0 });
    setStarted(true);
  };

  const reset = () => {
    setStarted(false);
    setMode(null);
    setDeck([]);
    setIdx(0);
    setStage(0);
    setTally({ learned: 0, review: 0, unknown: 0, new: 0 });
  };

  const total = deck.length;
  const progress = total ? (idx / total) * 100 : 0;
  const finished = started && idx >= total;

  const flip = () => setStage((s) => (s < 2 ? s + 1 : 0));

  const judge = (category) => {
    if (!current) return;
    wordsApi.judge(current.id, category);
    setTally((t) => ({ ...t, [category]: t[category] + 1 }));
    if (idx + 1 >= total) {
      sessionsApi.record({
        mode,
        total,
        tally: { ...tally, [category]: tally[category] + 1 },
      });
      setIdx(total);
    } else {
      setIdx((i) => i + 1);
      setStage(0);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!started || finished) return;
    const h = (e) => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); flip(); }
      else if (e.key === '1') judge('learned');
      else if (e.key === '2') judge('review');
      else if (e.key === '3') judge('new');
      else if (e.key === '4') judge('unknown');
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line
  }, [started, finished, idx, stage, current]);

  // ----- RENDER --------------------------------------------------------
  if (words.length === 0) return <EmptyState />;
  if (!started) return <ModeSelector words={words} onStart={onStart} />;
  if (finished) return <FinishedState total={total} tally={tally} onReset={reset} />;

  return (
    <section className="max-w-4xl mx-auto px-6 sm:px-12 py-10">
      {/* Top bar: progress + counters */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs font-mono-plex tracking-[0.2em] uppercase mb-2">
          <span>{idx + 1} / {total}</span>
          <span className="hidden sm:flex items-center gap-3">
            <Tag c="emerald" label={`Öğrendim ${tally.learned}`} />
            <Tag c="sky"     label={`Kolay ${tally.review}`} />
            <Tag c="orange"  label={`İlk Defa ${tally.new}`} />
            <Tag c="rose"    label={`Zor ${tally.unknown}`} />
          </span>
        </div>
        <div
          data-testid={STUDY.progressBar}
          className="h-1.5 w-full bg-muted overflow-hidden"
        >
          <motion.div
            className="h-full bg-foreground"
            animate={{ width: `${progress}%` }}
            transition={{ ease: 'easeOut', duration: 0.5 }}
          />
        </div>
      </div>

      {/* Mode banner */}
      <div className="mb-6 flex items-center justify-between">
        <div className="overline">
          MODUS — {mode === 'de-tr' ? 'DE → TR' : 'TR → DE'}
        </div>
        <button
          onClick={reset}
          data-testid={STUDY.resetBtn}
          className="overline hover:text-foreground text-muted-foreground inline-flex items-center gap-1"
        >
          <ArrowsClockwise size={14} weight="bold" /> Modu Değiştir
        </button>
      </div>

      {/* The flashcard */}
      <Flashcard word={current} mode={mode} stage={stage} onFlip={flip} />

      {/* 4-category controls */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {CATEGORY_BUTTONS.map((b) => (
          <button
            key={b.key}
            data-testid={b.testId}
            onClick={() => judge(b.key)}
            className={`btn-tactile group h-20 border text-white font-medium tracking-wide flex flex-col items-center justify-center gap-0.5 ${b.cls}`}
          >
            <span className="font-display font-black text-lg sm:text-xl tracking-tight">
              {b.label}
            </span>
            <span className="text-[10px] tracking-[0.2em] uppercase opacity-80 font-mono-plex">
              {b.hint}
            </span>
          </button>
        ))}
      </div>

      <button
        data-testid={STUDY.flipBtn}
        onClick={flip}
        className="mt-3 w-full h-11 border border-foreground btn-tactile inline-flex items-center justify-center gap-2 text-sm tracking-wide hover:bg-foreground hover:text-background transition-colors"
      >
        {stage < 2
          ? <>İleri ({stage + 1}/3) <ArrowRight size={16} weight="bold" /></>
          : <>Yeniden <ArrowsClockwise size={16} weight="bold" /></>}
      </button>

      <p className="mt-3 text-[11px] text-muted-foreground font-mono-plex tracking-wider text-center">
        KISA YOL — Boşluk/Enter: çevir · 1: Öğrendim · 2: Kolay · 3: İlk defa · 4: Zor
      </p>
    </section>
  );
}

// ----- Helpers --------------------------------------------------------------
function Tag({ c, label }) {
  const tone = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    sky: 'text-sky-600 dark:text-sky-400',
    amber: 'text-amber-700 dark:text-amber-400',
    rose: 'text-rose-600 dark:text-rose-400',
    orange: 'text-orange-600 dark:text-orange-400',
  }[c];
  return <span className={tone}>{label}</span>;
}

function FinishedState({ total, tally, onReset }) {
  return (
    <section data-testid={STUDY.finishedState} className="max-w-3xl mx-auto px-6 sm:px-12 py-20">
      <div className="overline mb-3">SITZUNG · ABGESCHLOSSEN</div>
      <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-none mb-8">
        Geschafft.
      </h1>
      <div className="grid grid-cols-2 sm:grid-cols-5 border-y border-border">
        <Stat label="Toplam" value={total} />
        <Stat label="Öğrendim"    value={tally.learned} accent="text-emerald-600 dark:text-emerald-400" />
        <Stat label="Kolay"       value={tally.review}  accent="text-sky-600 dark:text-sky-400" />
        <Stat label="İlk Defa"    value={tally.new}     accent="text-orange-600 dark:text-orange-400" />
        <Stat label="Zor"         value={tally.unknown} accent="text-rose-600 dark:text-rose-400" />
      </div>
      <div className="mt-10 flex gap-3">
        <Button data-testid={STUDY.resetBtn} onClick={onReset} className="rounded-none h-12 px-6">
          <ArrowsClockwise size={18} weight="bold" className="mr-2" /> Yeni Tur
        </Button>
        <Button variant="outline" asChild className="rounded-none h-12 px-6">
          <Link to="/dashboard">Panele Git</Link>
        </Button>
      </div>
    </section>
  );
}

function Stat({ label, value, accent = '' }) {
  return (
    <div className="p-6">
      <div className="overline mb-2">{label}</div>
      <div className={`font-display font-black text-3xl sm:text-4xl tracking-tighter ${accent}`}>{value}</div>
    </div>
  );
}

function ModeSelector({ words, onStart }) {
  const due = useMemo(() => {
    const now = Date.now();
    return words.filter((w) => !w.nextDue || new Date(w.nextDue).getTime() <= now).length;
  }, [words]);
  const stubs = useMemo(() => words.filter(isStub).length, [words]);

  return (
    <section className="max-w-5xl mx-auto px-6 sm:px-12 py-16">
      <div className="overline mb-3">STUDIENRAUM</div>
      <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9] mb-4">
        Karten ziehen.<br/>
        <span className="text-primary">Wörter</span> behalten.
      </h1>
      <p className="text-muted-foreground max-w-xl mb-3 text-lg">
        {words.length} kart havuzunda · {due} bugün tekrar zamanı.
      </p>
      {stubs > 0 && (
        <p className="text-xs text-muted-foreground mb-12 font-mono-plex">
          {stubs} kart hâlâ KI tarafından zenginleştirilecek (çalıştıkça otomatik dolar).
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
        <ModeCard
          testId={STUDY.modeDeTr}
          eyebrow="MODUS 01"
          title="Deutsch → Türkçe"
          desc="Almanca kelime ilk yüzde belirir. Türkçe karşılığını ve ek detayları çevirmek için sen yönlendirirsin."
          onClick={() => onStart('de-tr')}
        />
        <ModeCard
          testId={STUDY.modeTrDe}
          eyebrow="MODUS 02"
          title="Türkçe → Deutsch"
          desc="Türkçe başlar. Almanca artikel, çekim ve cümleleri zihninde toparla."
          onClick={() => onStart('tr-de')}
          accent
        />
      </div>

      <div className="mt-8 text-xs text-muted-foreground">
        Aralık:&nbsp;
        Öğrendim {CATEGORY_INTERVAL_DAYS.learned} gün · Kolay {CATEGORY_INTERVAL_DAYS.review} gün ·
        İlk defa {CATEGORY_INTERVAL_DAYS.new} gün · Zor {CATEGORY_INTERVAL_DAYS.unknown} gün
      </div>
    </section>
  );
}

function ModeCard({ eyebrow, title, desc, onClick, testId, accent }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`group text-left p-10 sm:p-14 border-border transition-all duration-300 hover:bg-foreground hover:text-background ${accent ? 'md:border-l' : ''}`}
    >
      <div className="overline mb-6 group-hover:text-background/70">{eyebrow}</div>
      <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter leading-tight mb-4">
        {title}
      </h2>
      <p className="text-muted-foreground group-hover:text-background/80 max-w-sm mb-8">
        {desc}
      </p>
      <span className="inline-flex items-center gap-2 overline">
        Başla <ArrowRight size={16} weight="bold" />
      </span>
    </button>
  );
}

function EmptyState() {
  const reload = () => { wordsApi.reseed(); };
  return (
    <section data-testid={STUDY.emptyState} className="max-w-3xl mx-auto px-6 sm:px-12 py-24 text-center">
      <BookOpen size={56} weight="duotone" className="mx-auto mb-6 text-muted-foreground" />
      <div className="overline mb-3">LEERER STAPEL</div>
      <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mb-4">
        Henüz kart yok.
      </h1>
      <p className="text-muted-foreground mb-10 text-lg">
        Gömülü B2 sözlüğünü tekrar yükleyebilir ya da kendi kelimelerini
        ekleyebilirsin.
      </p>
      <div className="flex gap-3 justify-center">
        <Button data-testid={STUDY.reloadSeedBtn} onClick={reload} className="rounded-none h-12 px-6">
          <Sparkle size={18} weight="duotone" className="mr-2" /> B2 Sözlüğünü Yükle
        </Button>
        <Button asChild variant="outline" className="rounded-none h-12 px-6">
          <Link to="/add">
            <ArrowsHorizontal size={18} weight="bold" className="mr-2" /> Kelime Ekle
          </Link>
        </Button>
      </div>
    </section>
  );
}
